<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-50">
    <div class="w-full max-w-xl p-4">
      <vs-card v-if="formSubmitted">
        <div class="flex justify-center">
          <div class="bg-green-300 rounded-full p-4 animate-scale-in">
            <feather-icon
              icon="CheckIcon"
              svgClasses="w-10 h-10 hover:text-primary stroke-current text-success"
            />
          </div>
        </div>
        <h2 class="text-center font-semibold text-gray-800 mb-2">Thank you!</h2>
        <p class="text-center text-xl text-gray-600 mb-6">
          This feedback has been successfully submitted.
        </p>
      </vs-card>
      <vs-card v-else class="shadow-lg py-4">
        <h1 class="font-bold mb-6 text-center">{{ headingText }}</h1>
        <!-- Loop through each question -->
        <div
          v-for="field in fields"
          :key="field.id"
          class="mb-6 p-4 rounded border border-gray-200"
        >
          <h3 class="text-base font-semibold mb-3">
            {{ field.questionText }}
          </h3>

          <!-- TEXT input -->
          <vs-textarea
            v-if="field.inputType === 'TEXT'"
            v-model="fieldsData[field.id]"
            placeholder="Type your response"
            class="mb-2"
          />

          <!-- CHECKBOX group -->
          <div v-else-if="field.inputType === 'CHECKBOX'">
            <vs-checkbox
              v-for="option in field.options"
              :key="option"
              :value="checkboxData[field.id].includes(option)"
              @change="onCheckboxChange(field.id, option, $event)"
              class="mr-3 my-1"
            >
              {{ option }}
            </vs-checkbox>
          </div>

          <!-- YES/NO radio buttons -->
          <div v-else-if="field.inputType === 'YES_NO'">
            <vs-radio
              :vs-name="`yes_no_${field.id}`"
              v-model="radioData[field.id]"
              vs-value="Yes"
              class="mr-4"
            >
              Yes
            </vs-radio>
            <vs-radio
              :vs-name="`yes_no_${field.id}`"
              v-model="radioData[field.id]"
              vs-value="No"
            >
              No
            </vs-radio>
          </div>

          <!-- RATING stars -->
          <div v-else-if="field.inputType === 'RATING'">
            <span
              v-for="n in 5"
              :key="n"
              class="text-2xl cursor-pointer mr-1"
              :style="{ color: n <= ratingData[field.id] ? '#FFD700' : '#ccc' }"
              @click="!formSubmitted && (ratingData[field.id] = n)"
              :class="{
                'cursor-pointer': !formSubmitted,
                'opacity-50 cursor-not-allowed': formSubmitted,
              }"
            >
              ★
            </span>
          </div>
        </div>

        <div>
          <h3 class="text-base font-semibold mb-3">Rate Overall Experience</h3>
          <span
            v-for="n in 5"
            :key="n"
            class="text-2xl cursor-pointer mr-1"
            :style="{ color: n <= rating ? '#FFD700' : '#ccc' }"
            @click="!formSubmitted && (rating = n)"
            :class="{
              'cursor-pointer': !formSubmitted,
              'opacity-50 cursor-not-allowed': formSubmitted,
            }"
          >
            ★
          </span>
        </div>
        <div>
          <h3 class="text-base font-semibold mb-3">Comment</h3>
          <vs-textarea
            v-model="comment"
            placeholder="Type your comment"
            class="mb-2"
            rows="3"
          />
        </div>

        <!-- Submit Button -->
        <div v-show="!formSubmitted" class="text-center">
          <vs-button @click="submitResponses">Submit</vs-button>
        </div>
      </vs-card>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "DynamicForm",

  data() {
    return {
      checkboxData: {},
      radioData: {},
      formSubmitted: false,
      fieldsData: {},
      ratingData: {},
      rating: null,
      comment: "",
      submittedAnswers: [],
      fields: [],
      headingText: "",
    };
  },
  computed: {
    ...mapState({
      questionsList: (state) => state.customer.questionsList,
    }),
  },
  methods: {
    cancel() {
      this.goBack();
    },
    goBack() {
      this.$router.go(-1);
    },
    async submitResponses() {
      try {
        const feedbackArray = this.fields.map((field) => {
          let answer = "";
          if (field.inputType === "CHECKBOX") {
            answer = this.checkboxData[field.id].toString();
          } else if (field.inputType === "YES_NO") {
            answer = this.radioData[field.id];
          } else if (field.inputType === "RATING") {
            answer = this.ratingData[field.id].toString();
          } else if (field.inputType === "TEXT") {
            answer = this.fieldsData[field.id];
          }
          return {
            questionnaireId: field.id,
            answer: answer,
          };
        });

        const payload = {
          id: this.$route.params.id,
          data: {
            encryptedString: this.$route.params.codeId,
            rating: this.rating,
            comment: this.comment,
            feedback: feedbackArray,
          },
        };
        await this.$store.dispatch("customer/createFeedbackAction", payload);
        this.$swal({
          title: "Success",
          text: "Feedback submitted successfully!",
          icon: "success",
        });
        this.formSubmitted = true;
      } catch (error) {
        const isAlreadySubmitted =
          error.message.defaultUserMessage ===
          "Unknown data integrity issue with feedback: Feedback has already been submitted for this campaign";
        this.$swal({
          title: "Error",
          text: isAlreadySubmitted
            ? "Feedback has already been submitted for this campaign."
            : "Failed to submit feedback. Please try again.",
          icon: "error",
        });
      }
    },
    async getFields() {
      await this.$store.dispatch(
        "customer/getFeedbackQuestionsAction",
        this.$route.params.codeId
      );
      if (this.questionsList.status == "SENT") {
        this.fields = this.questionsList && this.questionsList.questions;
        this.headingText = "Feedback Form";
        this.formSubmitted = false;
      } else {
        this.headingText = "Feedback Response";
        this.formSubmitted = true;
        this.rating = this.questionsList.response.rating;
        this.comment = this.questionsList.response.comment;
        this.fields =
          this.questionsList &&
          this.questionsList.response &&
          this.questionsList.response.answers;
      }

      this.fields.forEach((field) => {
        const answerText = field.answerText || "";

        if (field.inputType === "CHECKBOX") {
          const selected = answerText
            ? answerText.split(",").map((item) => item.trim())
            : [];
          this.$set(this.checkboxData, field.id, selected);
        } else if (field.inputType === "YES_NO") {
          this.$set(this.radioData, field.id, answerText || "");
        } else if (field.inputType === "RATING") {
          this.$set(this.ratingData, field.id, parseInt(answerText) || 0);
        } else if (field.inputType === "TEXT") {
          this.$set(this.fieldsData, field.id, answerText || "");
        }
      });
    },
    onCheckboxChange(fieldId, option, event) {
      if (event) {
        this.checkboxData[fieldId] = [option];
      } else {
        this.checkboxData[fieldId] = [];
      }
    },
  },
  async mounted() {
    await this.getFields();
  },
};
</script>

<style scoped>
body {
  font-family: "Inter", sans-serif;
}
</style>
